import Preview from "./preview";
import {select} from "d3-selection";

import {
    alignBelowAnnotations,
    linearDomain,
    mergeAnnotations,
    rangeOverlapStart,
    scaleAnnotationSize,
    attributeAnnotations,
    dnaClassColor, alignBpPosition,
} from "./utils";

import {
    radianFromBasePair,
    spanPath,
    annotationPath,
    pointFromBasePair,
    getBasePairFromPoint,
} from "../mixins/circularShape";

const d3 = {select};

import Brush from "../mixins/circularBrush";
import {Selector} from "../mixins/dictionaries";

import {circularPreviewHeight, linearPreviewHeight} from '../mixins/constants';

export default class CircularPreview extends Preview {
    constructor(settings, mediator) {
        super(settings, mediator)

        this.EventsOn();

        this.initContainer();
        this.brush = new Brush();

        this.brush.init(this.previewBoxContainer);
        this.brush.create();
    }

    initData(data) {
        super.initDataSequence(data)
        this.setComponentPositions(this.maxBelowLevel, this.maxAboveLevel);
        this.domainX = linearDomain([0, this.sequenceLength], this.circle.circumference);
        this.initPosition = 0;
    }

    initContainer() {
        const $containerPreview = document.querySelector(Selector.PLASMID);
        $containerPreview.style.height = `${circularPreviewHeight}px`;
        this.$previewBoxContainer = $containerPreview.getBoundingClientRect();

        this.previewBox = d3.select(Selector.PLASMID).append("svg")
            .attr("viewBox", `0 0 ${$containerPreview.clientWidth} ${$containerPreview.clientHeight}`)
            .style("width", "100%")
            .style("height", "auto");

        this.previewBoxContainer = this.previewBox
            .append("g")

        this.addEventsContainer();
    }

    setComponentPositions(belowLevels = -1, aboveLevels = -1) {
        const initYPosition = 5;
        aboveLevels += 1;

        this.stepLevelAnnotation = 2;
        this.annotationHeight = 6;

        const aboveAnnotationHeight = aboveLevels * (this.stepLevelAnnotation + this.annotationHeight)

        const slotHeight = (this.srcData.slots && this.srcData.slots.length) ? (this.stepLevelAnnotation + this.annotationHeight) : 0;

        const centerPreview = {
            x: this.$previewBoxContainer.width / 2,
            y: this.$previewBoxContainer.height / 2
        };

        let radius = +(Math.min(this.$previewBoxContainer.width, circularPreviewHeight) / 2) - slotHeight - initYPosition;
        radius -= aboveAnnotationHeight;

        this.circle = {
            circumference: (2 * Math.PI) * radius,
            radius: radius,
            center: centerPreview,
        }

        this.positionsY = {
            ruler: this.circle.radius,
            slots: this.circle.radius + this.annotationHeight + this.stepLevelAnnotation,
            restrictionEnzymes: this.circle.radius,
            annotations: {
                above: this.circle.radius + slotHeight + 10,
                below: this.circle.radius - 20
            },
        };

        this.regularFontSizeWidth = this.circle.circumference / 180;
        this.fontBoxWidth = this.circle.circumference / this.sequenceLength;

        this.previewBoxContainer
            .attr("transform", "translate(" + centerPreview.x + ", " + centerPreview.y + ")");

        this.brush.setUp(this.circle, radius, this.sequenceLength);
    }

    setData(data) {
        this.initData(data)
        this.render();
    }

    render() {

        this.drawSlots();
        this.drawRuler();
        this.drawSequence();
        this.drawAnnotations();
    }

    drawSlots() {
        const slotHeight = 5;
        let start = 0;
        let end = 0;

        this.previewBoxContainer.selectAll(".slot").remove();

        this.srcData.slots.forEach(s => {
            end = start + s.length;
            if (s.length) {
                this.drawSlot(this.previewBoxContainer, s, start, end, slotHeight);
                start += s.length;
            }

            start = end;
        })
    }

    drawSlot(container, s, slotStart, slotEnd, height) {
        const [start, end, arrowLength, direction] = scaleAnnotationSize(slotStart, slotEnd, s.direction, 6, this.fontBoxWidth, this.regularFontSizeWidth, this.sequenceLength);
        const point = pointFromBasePair(slotStart, this.circle.radius, this.sequenceLength);

        container
            .append("path")
            .attr("class", "slot")
            .attr("d", annotationPath(this.positionsY.slots, height, start, end, this.sequenceLength, direction, arrowLength))
            .style("stroke", "#000")
            .style("stroke-width", .5)
            .style("opacity", 1);

        // edge
        container
            .append("line")
            .attr("class", "slot")
            .attr("x1", point.x)
            .attr("y1", point.y)
            .attr("x2", 0)
            .attr("y2", 0)
            .style("stroke-dasharray", ("3, 3"));

        // is editable, is public
        if (dnaClassColor(s.dna_sequence)) {
            container.append("path")
                .attr("class", "slot " + dnaClassColor(s.dna_sequence))
                .attr("d", spanPath(this.positionsY.slots - height, 0, radianFromBasePair(start + arrowLength, this.sequenceLength), radianFromBasePair(end - arrowLength, this.sequenceLength)))
                .attr('stroke-width', 1);
        }
    }

    drawRuler() {
        this.previewBoxContainer.selectAll(".tickGroup").remove();

        const ticks = this.domainX.ticks(10);
        const tickHeight = 8;
        ticks.forEach(t => {
            if (t) {
                const point = pointFromBasePair(t, this.circle.radius, this.sequenceLength);
                const tickContainer = this.previewBoxContainer.append("g")
                    .attr("class", "tickGroup")
                    .attr("transform", "translate(" + point.x + "," + point.y + ")rotate(" + ((360 / this.sequenceLength) * (t - 1)) + ")");

                this.drawTick(tickContainer, t, tickHeight)
            }
        })
    }

    drawTick(container, tickLabel, height) {
        container.append("line")
            .attr("class", "tickLine")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", 0)
            .attr("y2", height)
            .attr("stroke", "#000")
            .attr("stroke-width", 1)
            .attr("opacity", 1);

        container.append("text")
            .attr("class", "tickText")
            .attr("dy", (height * 2))
            .style("font-size", .7 + "em")
            .style('text-anchor', 'middle')
            .text(tickLabel)
    }

    drawSequence() {
        this.previewBoxContainer.select("#sequence").remove();
        this.previewBoxContainer.append("path")
            .attr("id", "sequence")
            .attr("d", spanPath(this.circle.radius, this.circle.radius, 0, 2 * Math.PI))
            .style("fill", "none")
            .style("stroke", "#000")
            .style("stroke-width", .5);
    }

    updateAnnotations(data) {
        this.maxBelowLevel = -1;
        this.maxAboveLevel = -1;

        [this.annotations, this.maxBelowLevel, this.maxAboveLevel] = alignBelowAnnotations(mergeAnnotations(data, this.previewAnnotations, this.protocolAnnotations, this.sequenceLength, this.hiddenAnnotations));

        if (this.maxAboveLevel > -1) {
            this.setComponentPositions(this.maxBelowLevel, this.maxAboveLevel);
            this.render()
            this.renderRestrictionEnzymes(this.restrictionEnzymes);
        } else {
            this.drawAnnotations();
        }

    }

    drawAnnotations() {
        const stepLevel = this.stepLevelAnnotation;
        const maxBelowLevels = this.maxBelowLevel + 1;
        this.annotationHeight = Math.min(this.annotationHeight, ((this.circle.radius - (stepLevel * maxBelowLevels)) / maxBelowLevels));
        let layer = 0;

        this.previewBoxContainer.selectAll(".annotation").remove();

        this.annotations.forEach(a => {
            layer = (stepLevel + this.annotationHeight) * a.level;
            if (a.placement === "above") {
                layer = this.positionsY.annotations.above + layer;
            } else {
                layer = this.positionsY.annotations.below - layer;
            }

            this.drawAnnotation(this.previewBoxContainer, a, this.annotationHeight, layer);
        });
    }

    drawAnnotation(container, a, height, layer) {
        const attributeAnnotation = attributeAnnotations(a.role_id, this.settings.annotation_colors);
        const [start, end, arrowLength, direction] = scaleAnnotationSize(a.start, a.end, a.direction, 6, this.fontBoxWidth, this.regularFontSizeWidth, this.sequenceLength);

        container
            .append("path")
            .attr("class", "annotation annotation_" + a.pk)
            .attr("d", annotationPath(layer, height, start, end, this.sequenceLength, direction, arrowLength))
            .attr("fill", attributeAnnotation.color)
            .attr("stroke", "#000")
            .attr("stroke-width", 0)
            .style("opacity", .6);
    }

    renderRestrictionEnzymes(data) {
        this.restrictionEnzymes = data;
        const initHeight = this.srcData.slots && this.srcData.slots.length ? 7 : 4;
        const restEnzymeHeight = 4;
        let height = 0;
        this.previewBoxContainer.selectAll(".restEnzymes").remove();

        this.restrictionEnzymes.forEach(r => {
            height = initHeight + ((r.level + 1) * restEnzymeHeight)
            const point = pointFromBasePair(r.cut_5, this.circle.radius, this.sequenceLength);

            const restEnzymeContainer = this.previewBoxContainer
                .append("g")
                .attr("class", "restEnzymes")
                .attr("transform", "translate(" + point.x + "," + point.y + ")rotate(" + ((360 / this.sequenceLength) * (r.cut_5 - 1)) + ")");

            this.drawRestrictionEnzyme(restEnzymeContainer, r, height)
        })
    }

    drawRestrictionEnzyme(container, re, height) {
        container
            .append("line")
            .attr("class", "restEnzyme restEnzyme_" + re.id)
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", 0)
            .attr("y2", -height)
            .attr("stroke", "#000")
            .attr("stroke-width", .5)
            .style("opacity", re.visible ? 1 : 0)
    }

    addEventsContainer() {
        this.previewBox
            .on("contextmenu", (e) => {
                e.preventDefault();
                e.stopPropagation();
            })
            .on("click", (e) => {
                e.preventDefault();
                e.stopPropagation();

                this.offsetScroll.current = Math.round(getBasePairFromPoint(e, this.circle, this.$previewBoxContainer, this.sequenceLength));

                this.EventsTrigger("hideContextMenu");
                this.EventsTrigger("setOffsetScroll", this.offsetScroll.current)
                this.EventsTrigger("slide", "end")

                this.slide();
            })
    }

    setBrushSize(data) {
        this.maxBasePairsInAreaView = data.width === this.sequenceLength ? 0 : data.width;
        this.setOffsetScroll(data.position);
    }

    setOffsetScroll(data) {
        this.offsetScroll.current = data;

        let startBrush = alignBpPosition(this.offsetScroll.current, -(this.maxBasePairsInAreaView / 2));
        let endBrush = rangeOverlapStart(startBrush, startBrush + this.maxBasePairsInAreaView, this.sequenceLength, this.sequenceLength);

        this.rangeBp = {
            start: startBrush,
            end: endBrush,
        };

        this.brush.setParameters({
            start: radianFromBasePair(this.rangeBp.start, this.sequenceLength),
            end: radianFromBasePair(this.rangeBp.end, this.sequenceLength),
            direction: 1
        });
    }

    setScroll(scroll) {
        this.offsetScroll.current -= scroll;
    }

    slide() {
        let startBrush = alignBpPosition(this.offsetScroll.current, -(this.maxBasePairsInAreaView / 2));
        let endBrush = rangeOverlapStart(startBrush, startBrush + this.maxBasePairsInAreaView, this.sequenceLength, this.sequenceLength);

        this.rangeBp = {
            start: startBrush,
            end: endBrush,
        };

        this.brush.setParameters({
            start: radianFromBasePair(this.rangeBp.start, this.sequenceLength),
            end: radianFromBasePair(this.rangeBp.end, this.sequenceLength),
            direction: 1
        });
    }

    toggleItems(data) {
        if (data.selector === "restrictionEnzymes") {
            this.restrictionEnzymes.forEach(el => {
                if (data.objects.includes(el.id)) {
                    if (!data.visible) {
                        this.hiddenRestrictionEnzymes.push(el.id)
                    } else {
                        this.hiddenRestrictionEnzymes = this.hiddenRestrictionEnzymes.filter(item => item !== el.id)
                    }
                    el.visible = data.visible;

                    this.previewBox.selectAll(".restEnzyme_" + el.id)
                        .style("opacity", data.visible ? 1 : 0);
                }
            })
        } else {
            this.annotations.forEach(el => {
                if (data.objects.includes(el.pk)) {
                    if (!data.visible) {
                        this.hiddenAnnotations.push(el.pk)
                    } else {
                        this.hiddenAnnotations = this.hiddenAnnotations.filter(item => item !== el.pk)
                    }

                    el.visible = data.visible;
                    this.previewBox.selectAll(".annotation_" + el.pk)
                        .style("opacity", data.visible ? .6 : 0);
                }
            })
        }
    }

    EventsOn() {
        super.EventsOn();
        if (this.mediator) {
            this.mediator.on("setData", "setData", this);
            this.mediator.on("setAreaView", "setBrushSize", this);
            this.mediator.on("setOffsetScroll", "setOffsetScroll", this);
            this.mediator.on("setScroll", "setScroll", this);
            this.mediator.on("slide", "slide", this);

            this.mediator.on("updateAnnotations", "updateAnnotations", this);
            this.mediator.on("loadRestrictionEnzymes", "renderRestrictionEnzymes", this);

            this.mediator.on("toggleObjects", "toggleItems", this);
        }
    }
}